import type { SelectStore } from '@ariakit/react/select';
import { createContext, use, useMemo } from 'react';

type SelectContextValue = {
  store: SelectStore;
};

const SelectContext = createContext<SelectContextValue | undefined>(undefined);

type SelectProviderProps = SelectContextValue & {
  children: React.ReactNode;
};

/**
 * {@link SelectProvider} is a context provider which allows more flexible component composition for
 * Select components by providing the ariakit `store` to nested children (e.g. SelectItem).
 */
export function SelectProvider({ store, children }: SelectProviderProps) {
  const value = useMemo(() => ({ store }), [store]);

  return <SelectContext value={value}>{children}</SelectContext>;
}

/**
 * This hook lets us expose our modal context in a type safe way without needing to supply
 * default arguments to React.createContext, which would be nonsensical.
 *
 * See: https://kentcdodds.com/blog/how-to-use-react-context-effectively#typescript--flow
 */
export function useSelect() {
  const context = use(SelectContext);
  if (context === undefined) {
    throw new Error('useSelect must be used within a Select component');
  }
  return context;
}
